import React from 'react';
import {connect} from 'react-redux';
import InputRadio from 'components/atoms/inputs/input-radio';
import SimpleSelect from 'components/atoms/dropdowns/simple-select';
import {SimpleSelect as SimpleSelectize} from 'react-selectize';
import InputText from 'components/atoms/inputs/input-text';
import MultiSelect from 'components/atoms/dropdowns/multi-select';
import {generateSignature} from 'helpers/hash';
import EnableSwitch from 'components/organisms/projection-boost-rule/enable-switch';
import AbstractSettings, {sortPlayers, getPlayerOptionValue, getPositionOptionValue} from './../abstract-settings';
import SelectKeyPlayerLineupPosition from 'components/organisms/projection-boost-rule/components/atoms/select-key-player-lineup-position';

class Settings extends AbstractSettings {
    getKeyPlayerView() {
        const {players} = this.props;

        const options = sortPlayers(Object.values(players.all())).map((player) => getPlayerOptionValue(player));

        const selectedPlayer = players.getPlayer(this.state.keyPlayer);

        return (
            <SimpleSelectize
                key={'keyPlayer'}
                defaultValue={''}
                onValueChange={this.onChangeKeyPlayer.bind(this)}
                options={options}
                value={getPlayerOptionValue(selectedPlayer)}
                placeholder={'Select player'}
            />
        );
    }

    getKeyPositionView() {
        const options = {'': 'Select position'};
        this.getPositionNames().map((position) => (options[position] = position));

        return (
            <SimpleSelect
                className="form-control"
                key={'keyPosition'}
                onChange={this.onChangeKeyPosition.bind(this)}
                options={options}
                value={this.state.keyPosition}
                placeholder={'Select position'}
            />
        );
    }

    getBoostedPlayerView(statementKey) {
        const {players} = this.props;
        const boostStatement = this.state.boostStatements[statementKey];

        const options = sortPlayers(Object.values(players.all())).map((player) => getPlayerOptionValue(player));

        const selectedPlayer = players.getPlayer(boostStatement.boostedPlayer);

        return (
            <SimpleSelectize
                key={`boostedPlayer-${statementKey}`}
                defaultValue={''}
                onValueChange={((value) => this.onChangeBoostedPlayer(statementKey, value)).bind(this)}
                options={options}
                value={getPlayerOptionValue(selectedPlayer)}
                placeholder={'Select player'}
            />
        );
    }

    getBoostedPositionsView(statementKey) {
        const boostStatement = this.state.boostStatements[statementKey];

        const options = this.getPositionNames().map((position) => getPositionOptionValue(position));

        const values = options
            .filter((option) => inArray(option.value, boostStatement.boostedPositions))
            .map((option) => option);

        return (
            <>
                <MultiSelect
                    key={`boostedPositions-${statementKey}`}
                    onSelect={((value) => this.onChangeBoostedPositions(statementKey, value)).bind(this)}
                    options={options}
                    values={values}
                    placeholder={'Select position'}
                />
                <div className="form-text">from</div>
                <SimpleSelect
                    className="form-control"
                    onChange={((value) => this.onChangeIsSameTeam(statementKey, value)).bind(this)}
                    options={{1: 'Same team', 0: 'Opposing team'}}
                    value={Number(boostStatement.isSameTeam)}
                />
            </>
        );
    }

    getBoostStatementView(statementKey) {
        const boostStatement = this.state.boostStatements[statementKey];

        return (
            <div className="form-inline" key={`boostStatement-${statementKey}`}>
                <SimpleSelect
                    className="form-control input-md"
                    value={boostStatement.boostType}
                    options={{decrease: 'Decrease', increase: 'Increase'}}
                    onChange={((value) => this.onChangeBoostType(statementKey, value)).bind(this)}
                />
                <div className="form-text">projection for</div>
                <label className="radio-inline">
                    <InputRadio
                        value={'player'}
                        checked={boostStatement.boostFor === 'player'}
                        onChange={((value) => this.onChangeBoostFor(statementKey, value)).bind(this)}
                    />
                    Player
                </label>
                <label className="radio-inline">
                    <InputRadio
                        value={'position'}
                        checked={boostStatement.boostFor === 'position'}
                        onChange={((value) => this.onChangeBoostFor(statementKey, value)).bind(this)}
                    />
                    Position
                </label>
                {boostStatement.boostFor === 'player'
                    ? this.getBoostedPlayerView(statementKey)
                    : this.getBoostedPositionsView(statementKey)}
                <div className="form-text">by</div>
                <InputText
                    key={`boostPercent-${generateSignature()}`}
                    className="input-sm"
                    percent
                    value={boostStatement.boostPercent}
                    onBlur={((value) => this.onBlurBoostPercent(statementKey, value)).bind(this)}
                />
                {this.getRemoveBoostStatementView(statementKey)}
            </div>
        );
    }

    getRemoveBoostStatementView(statementKey) {
        return (
            <span onClick={(() => this.onRemoveStatement(statementKey)).bind(this)} className="close-rule">
                <i className="fa fa-times-circle"></i>
            </span>
        );
    }

    render() {
        return (
            <div className="white-box">
                <div className="advanced-filter-heading">
                    <h3>Sim Boost Rules</h3>
                    <EnableSwitch />
                </div>
                <div className="gray-holder gap-y">
                    <div className="form-inline">
                        <div className="form-text">If</div>
                        <label className="radio-inline">
                            <InputRadio
                                value={'player'}
                                checked={this.state.statementType === 'player'}
                                onChange={this.onChangeStatementType.bind(this)}
                            />
                            Player
                        </label>
                        <label className="radio-inline">
                            <InputRadio
                                value={'position'}
                                checked={this.state.statementType === 'position'}
                                onChange={this.onChangeStatementType.bind(this)}
                            />
                            Position
                        </label>
                        {this.state.statementType === 'player' ? this.getKeyPlayerView() : this.getKeyPositionView()}

                        {this.canKeyPlayerLineupPositionBeRendered() ? (
                            <>
                                <div className="form-text">is</div>
                                <SelectKeyPlayerLineupPosition
                                    key={`pbr-kplp-${this.state.keyPlayerLineupPosition}`}
                                    value={this.state.keyPlayerLineupPosition}
                                    onChange={((value) => this.onSelectKeyPlayerLineupPosition(value)).bind(this)}
                                />
                                <div className="form-text">in lineup then</div>
                            </>
                        ) : (
                            <div className="form-text">is in lineup then</div>
                        )}
                    </div>

                    <div className="form-inline">
                        {Object.keys(this.state.boostStatements).map((key) => this.getBoostStatementView(key))}

                        <div className="advanced-filter-actions">
                            <button type="button" onClick={this.onAddStatement.bind(this)} className="btn btn-outline">
                                Add Statement
                            </button>
                            <button type="button" onClick={this.onAddRuleItem.bind(this)} className="btn">
                                Add Rule
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStatesToProps(state) {
    const {
        projectionBoostRule,
        sportConfig,
        playersData: {players},
    } = state;

    return {
        projectionBoostRule,
        sportConfig,
        players,
    };
}

export default connect(mapStatesToProps)(Settings);
